/**
 * In-browser API mock for the `contract-royalty-rates` prototype.
 *
 * Companion to src/lib/devAuth.ts. When the dev auth bypass is active (no real
 * Stytch keys / no Django backend on :8000), this patches window.fetch to serve
 * in-memory (localStorage-backed) data so the prototype is usable with no backend.
 *
 * Scope — this prototype is ONLY about the contracts screen (expanding it to add
 * different royalty rates). So we seed:
 *   - Contracts: list + detail + create + edit + delete (a rich set of dummy
 *     contracts so the table and detail view are populated).
 *   - Payees: list + detail (needed by the create/edit contract payee combobox).
 *   - Label: GET (so the org / default currency initialises).
 * Every OTHER API route returns an empty paginated response (GET) or {} (writes)
 * so the rest of the app stays empty but does not crash. This is intentional —
 * we don't need the rest of the app for this prototype.
 *
 * Installed only from seedDevAuth() (gated on import.meta.env.DEV + placeholder
 * keys, or VITE_DEV_AUTH_BYPASS=true), so it is inert in normal production builds.
 */
import { API_BASE_URL } from "@/lib/api/core";

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

const emptyList = { results: [], count: 0, next: null, previous: null };

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

// --- Payees ----------------------------------------------------------------
// Used by the create/edit-contract payee combobox and the contract detail view.
const DEV_PAYEES = [
  {
    id: "payee-adele",
    name: "Adele Laurie Blue Adkins",
    legal_name: "Melted Stone Ltd",
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-weeknd",
    name: "Abel Makkonen Tesfaye",
    legal_name: "The Weeknd XO Inc",
    is_active: true,
    preferred_currency: "USD",
    country: "CA",
    city: "Toronto",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-tame",
    name: "Kevin Parker",
    legal_name: "Tame Impala Pty Ltd",
    is_active: true,
    preferred_currency: "AUD",
    country: "AU",
    city: "Perth",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-fkatwigs",
    name: "Tahliah Debrett Barnett",
    legal_name: "FKA twigs Ltd",
    is_active: true,
    preferred_currency: "GBP",
    country: "GB",
    city: "Gloucester",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-blood",
    name: "Benjamin Levin",
    legal_name: "Benny Blanco Publishing",
    is_active: true,
    preferred_currency: "USD",
    country: "US",
    city: "Los Angeles",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-flume",
    name: "Harley Edward Streten",
    legal_name: "Flume Music Pty Ltd",
    is_active: true,
    preferred_currency: "AUD",
    country: "AU",
    city: "Sydney",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
];

function payeeName(id: string): string {
  return DEV_PAYEES.find((p) => p.id === id)?.name ?? "Unknown payee";
}

// --- Seed contracts --------------------------------------------------------
// A believable spread across contract types / statuses / territories so the
// contracts table and detail view are fully populated for the prototype.
type SeedPayee = { id: string; share: number };

function contractPayees(payees: SeedPayee[]) {
  return payees.map(({ id, share }) => {
    const p = DEV_PAYEES.find((x) => x.id === id);
    return {
      matched: true,
      payee: {
        id,
        name: p?.name ?? "Unknown payee",
        legal_name: p?.legal_name ?? null,
        city: p?.city ?? null,
        country: p?.country ?? null,
      },
      share_percentage: share,
      opening_balance: null,
    };
  });
}

function seedContract(opts: {
  id: string;
  name: string;
  contract_type: string;
  status: string;
  start_date: string;
  end_date: string | null;
  territory: string;
  currency?: string;
  party?: string;
  label_share_percent?: string;
  payment_threshold?: string;
  advance_amount?: string;
  statement_period?: string;
  tracks_count?: number;
  first_sound_recording_title?: string | null;
  label_share_overrides?: Record<string, string> | null;
  payees: SeedPayee[];
}) {
  return {
    id: opts.id,
    name: opts.name,
    contract_type: opts.contract_type,
    status: opts.status,
    start_date: opts.start_date,
    end_date: opts.end_date,
    territory: opts.territory,
    currency: opts.currency ?? "USD",
    label: "dev-label",
    label_name: "Dev Label",
    party: opts.party ?? "Dev Label",
    label_share_percent: opts.label_share_percent ?? null,
    payment_threshold: opts.payment_threshold ?? null,
    advance_amount: opts.advance_amount ?? null,
    statement_period: opts.statement_period ?? "quarterly",
    starting_balance: null,
    cross_collateralized: false,
    notes: "",
    extracting_status: null,
    tracks_count: opts.tracks_count ?? 0,
    all_tracks_have_isrc: true,
    first_sound_recording_title: opts.first_sound_recording_title ?? null,
    label_share_overrides: opts.label_share_overrides ?? null,
    can_delete: true,
    // A contract cannot go Active without someone to pay, so the header's
    // "Activate contract" button is live only on a draft that has payees.
    can_activate: (opts.payees?.length ?? 0) > 0,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-06-01T00:00:00Z",
    payees: contractPayees(opts.payees),
    // Detail-endpoint extras.
    related_recordings: [],
    related_releases: [],
    has_revenue: false,
    has_expenses: false,
  };
}

const DEV_SEED_CONTRACTS = [
  seedContract({
    id: "contract-adele-30",
    name: "Adele — 30 Recording Agreement",
    contract_type: "recording",
    status: "active",
    start_date: "2021-11-19",
    end_date: null,
    territory: "Worldwide",
    party: "Melted Stone Ltd",
    label_share_percent: "80.00",
    payment_threshold: "100.00",
    advance_amount: "500000.00",
    statement_period: "half_yearly",
    tracks_count: 12,
    first_sound_recording_title: "Easy on Me",
    payees: [{ id: "payee-adele", share: 100 }],
  }),
  seedContract({
    id: "contract-weeknd-dawnfm",
    name: "The Weeknd — Dawn FM License",
    contract_type: "exclusive_license",
    status: "active",
    start_date: "2022-01-07",
    end_date: "2027-01-06",
    territory: "Worldwide",
    party: "The Weeknd XO Inc",
    label_share_percent: "75.00",
    payment_threshold: "50.00",
    statement_period: "quarterly",
    tracks_count: 16,
    first_sound_recording_title: "Sacrifice",
    payees: [{ id: "payee-weeknd", share: 100 }],
  }),
  seedContract({
    id: "contract-tame-currents",
    name: "Tame Impala — Currents Distribution",
    contract_type: "distribution",
    status: "active",
    start_date: "2015-07-17",
    end_date: null,
    territory: "Australia & New Zealand",
    currency: "AUD",
    party: "Tame Impala Pty Ltd",
    label_share_percent: "70.00",
    statement_period: "quarterly",
    tracks_count: 13,
    first_sound_recording_title: "Let It Happen",
    payees: [{ id: "payee-tame", share: 100 }],
  }),
  seedContract({
    id: "contract-fkatwigs-magdalene",
    name: "FKA twigs — MAGDALENE Recording Deal",
    contract_type: "recording",
    status: "active",
    start_date: "2019-11-08",
    end_date: null,
    territory: "Worldwide",
    currency: "GBP",
    party: "FKA twigs Ltd",
    label_share_percent: "78.00",
    payment_threshold: "75.00",
    statement_period: "quarterly",
    tracks_count: 9,
    first_sound_recording_title: "Cellophane",
    payees: [{ id: "payee-fkatwigs", share: 100 }],
  }),
  seedContract({
    id: "contract-flume-hires",
    name: "Flume — Hi This Is Flume Producer Agreement",
    contract_type: "producer",
    status: "active",
    start_date: "2019-03-20",
    end_date: null,
    territory: "Worldwide",
    currency: "AUD",
    party: "Flume Music Pty Ltd",
    label_share_percent: "60.00",
    statement_period: "quarterly",
    tracks_count: 17,
    first_sound_recording_title: "Rushing Back",
    // Saved overrides, so the edit form opens with the custom-rates section
    // already expanded: streaming is cut below the 60% label share, and the
    // label absorbs none of the expenses (recoup 100% from the payees).
    label_share_overrides: {
      streaming: "55.00",
      expenses: "0.00",
    },
    payees: [
      { id: "payee-flume", share: 70 },
      { id: "payee-blood", share: 30 },
    ],
  }),
  seedContract({
    id: "contract-blanco-friends",
    name: "Benny Blanco — Friends Keep Secrets 360 Deal",
    contract_type: "360",
    status: "suspended",
    start_date: "2018-12-14",
    end_date: null,
    territory: "North America",
    party: "Benny Blanco Publishing",
    label_share_percent: "50.00",
    advance_amount: "250000.00",
    statement_period: "quarterly",
    tracks_count: 8,
    first_sound_recording_title: "Eastside",
    payees: [
      { id: "payee-blood", share: 60 },
      { id: "payee-weeknd", share: 40 },
    ],
  }),
  seedContract({
    id: "contract-adele-sync",
    name: "Adele — Skyfall Sync Licensing",
    contract_type: "licensing",
    status: "expired",
    start_date: "2012-10-05",
    end_date: "2022-10-04",
    territory: "Worldwide",
    party: "Melted Stone Ltd",
    label_share_percent: "50.00",
    statement_period: "annually",
    tracks_count: 1,
    first_sound_recording_title: "Skyfall",
    payees: [{ id: "payee-adele", share: 100 }],
  }),
  seedContract({
    id: "contract-weeknd-feature",
    name: "The Weeknd — Featured Artist (Pray For Me)",
    contract_type: "featured_artist",
    status: "terminated",
    start_date: "2018-02-02",
    end_date: "2021-02-01",
    territory: "Worldwide",
    party: "The Weeknd XO Inc",
    label_share_percent: "50.00",
    statement_period: "quarterly",
    tracks_count: 1,
    first_sound_recording_title: "Pray For Me",
    payees: [
      { id: "payee-weeknd", share: 50 },
      { id: "payee-blood", share: 50 },
    ],
  }),
  seedContract({
    id: "contract-tame-borderline",
    name: "Tame Impala — Borderline Single Deal",
    contract_type: "recording",
    status: "draft",
    start_date: "2019-04-10",
    end_date: null,
    territory: "Worldwide",
    currency: "AUD",
    party: "Tame Impala Pty Ltd",
    statement_period: "quarterly",
    tracks_count: 2,
    first_sound_recording_title: "Borderline",
    payees: [{ id: "payee-tame", share: 100 }],
  }),
];

// --- User-created / edited contracts (persisted to localStorage) -----------
// Bump the version suffix when the seed shape changes so the browser re-seeds
// instead of serving a stale localStorage copy.
const CONTRACTS_STORE_KEY = "dev-mock-cr-contracts-v2";

function loadContracts(): any[] {
  try {
    const raw = localStorage.getItem(CONTRACTS_STORE_KEY);
    if (raw == null) {
      // First load: seed the store from the fixtures so edits/deletes persist.
      const seeded = JSON.parse(JSON.stringify(DEV_SEED_CONTRACTS));
      localStorage.setItem(CONTRACTS_STORE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    return JSON.parse(raw);
  } catch {
    return JSON.parse(JSON.stringify(DEV_SEED_CONTRACTS));
  }
}

function saveContracts(list: any[]): void {
  localStorage.setItem(CONTRACTS_STORE_KEY, JSON.stringify(list));
}

// Build a fully-shaped Contract from a POST/PUT body, merging onto an existing
// record for updates.
function buildContract(body: any, existing?: any): any {
  const now = new Date().toISOString();
  const payees = Array.isArray(body?.payees)
    ? body.payees.map((p: any) => ({
        matched: true,
        payee: {
          id: p.payee,
          name: payeeName(p.payee),
          legal_name: DEV_PAYEES.find((x) => x.id === p.payee)?.legal_name ?? null,
          city: DEV_PAYEES.find((x) => x.id === p.payee)?.city ?? null,
          country: DEV_PAYEES.find((x) => x.id === p.payee)?.country ?? null,
        },
        share_percentage: p.share_percentage ?? null,
        opening_balance: p.opening_balance ?? null,
      }))
    : (existing?.payees ?? []);

  return {
    id: existing?.id ?? crypto.randomUUID(),
    created_at: existing?.created_at ?? now,
    updated_at: now,
    name: body?.name ?? existing?.name ?? "Untitled contract",
    contract_type: body?.contract_type ?? existing?.contract_type ?? "recording",
    status: body?.status ?? existing?.status ?? "draft",
    start_date: body?.start_date ?? existing?.start_date ?? null,
    end_date: body?.end_date ?? existing?.end_date ?? null,
    territory: body?.territory ?? existing?.territory ?? "Worldwide",
    currency: body?.currency ?? existing?.currency ?? "USD",
    label: body?.label ?? existing?.label ?? "dev-label",
    label_name: "Dev Label",
    party: existing?.party ?? "Dev Label",
    label_share_percent:
      body?.label_share_percent != null
        ? String(body.label_share_percent)
        : (existing?.label_share_percent ?? null),
    payment_threshold: body?.payment_threshold ?? existing?.payment_threshold ?? null,
    advance_amount: body?.advance_amount ?? existing?.advance_amount ?? null,
    // Per-category label share overrides, keyed by revenue type or "expenses".
    // A PATCH replaces the whole map, including an explicit null that clears it.
    label_share_overrides:
      body?.label_share_overrides !== undefined
        ? body.label_share_overrides
        : (existing?.label_share_overrides ?? null),
    statement_period: body?.statement_period ?? existing?.statement_period ?? "quarterly",
    starting_balance: existing?.starting_balance ?? null,
    cross_collateralized: body?.cross_collateralized ?? existing?.cross_collateralized ?? false,
    notes: body?.notes ?? existing?.notes ?? "",
    extracting_status: null,
    payees,
    tracks_count: existing?.tracks_count ?? 0,
    all_tracks_have_isrc: true,
    first_sound_recording_title: existing?.first_sound_recording_title ?? null,
    can_delete: true,
    related_recordings: existing?.related_recordings ?? [],
    related_releases: existing?.related_releases ?? [],
    has_revenue: false,
    has_expenses: false,
  };
}

// --- Label (org / default currency) ----------------------------------------
const LABEL_STORE_KEY = "dev-mock-cr-label-v1";

function loadLabel(): any {
  const raw = localStorage.getItem(LABEL_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      // fall through to re-seed
    }
  }
  const label = {
    id: "dev-label",
    name: "Dev Label",
    currency: "USD",
    country: "US",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
  };
  localStorage.setItem(LABEL_STORE_KEY, JSON.stringify(label));
  return label;
}

// --- Reset (prototype affordance) ------------------------------------------
// Wipes every in-memory store this prototype owns so the next load re-seeds.
export function resetPrototype(): void {
  for (const key of [CONTRACTS_STORE_KEY, LABEL_STORE_KEY]) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem("organization-selection");
}

// Returns a Response for handled routes, or null to fall through to real fetch.
function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // --- Label -----------------------------------------------------------------
  if (trimmed === "/accounts/labels" && method === "GET") {
    return jsonResponse(paginated([loadLabel()]));
  }
  if (trimmed.startsWith("/accounts/labels/")) {
    const id = trimmed.slice("/accounts/labels/".length);
    const label = loadLabel();
    if (id !== label.id) return jsonResponse({ detail: "Not found." }, 404);
    if (method === "GET") return jsonResponse(label);
  }

  // --- Payees (match BEFORE /contracts/:id) ----------------------------------
  if (trimmed === "/contracts/payees" && method === "GET") {
    return jsonResponse(paginated(DEV_PAYEES));
  }
  if (trimmed.startsWith("/contracts/payees/")) {
    const id = trimmed.slice("/contracts/payees/".length);
    const found = DEV_PAYEES.find((p) => p.id === id);
    return found ? jsonResponse(found) : jsonResponse({ detail: "Not found." }, 404);
  }

  // --- Contracts -------------------------------------------------------------
  if (trimmed === "/contracts") {
    if (method === "GET") {
      let list = loadContracts();
      const q = (query.get("q") ?? query.get("search") ?? "").toLowerCase();
      if (q) {
        list = list.filter((c) =>
          String(c.name ?? "")
            .toLowerCase()
            .includes(q),
        );
      }
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((c) => allowed.has(c.status));
      }
      const typeParam = query.get("contract_type");
      if (typeParam) {
        const allowed = new Set(typeParam.split(","));
        list = list.filter((c) => allowed.has(c.contract_type));
      }
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        list = [...list].sort((a, b) => {
          const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, {
            numeric: true,
          });
          return desc ? -cmp : cmp;
        });
      }
      return jsonResponse(paginated(list));
    }
    if (method === "POST") {
      const list = loadContracts();
      const created = buildContract(body);
      list.unshift(created);
      saveContracts(list);
      return jsonResponse(created, 201);
    }
  }
  if (trimmed.startsWith("/contracts/")) {
    const id = trimmed.slice("/contracts/".length);
    const list = loadContracts();
    const idx = list.findIndex((c) => c.id === id);

    if (method === "GET") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      return jsonResponse(list[idx]);
    }
    if (method === "PUT" || method === "PATCH") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      const updated = buildContract(body, list[idx]);
      list[idx] = updated;
      saveContracts(list);
      return jsonResponse(updated);
    }
    if (method === "DELETE") {
      if (idx !== -1) {
        list.splice(idx, 1);
        saveContracts(list);
      }
      return jsonResponse(null, 204);
    }
  }

  // Unhandled API route: keep the (intentionally empty) rest of the app alive.
  console.warn(`[cr-mock] Unhandled ${method} ${path} — returning empty response.`);
  return method === "GET" ? jsonResponse(emptyList) : jsonResponse({});
}

export function installDevApiMock(): void {
  const w = window as unknown as { __devApiMockInstalled?: boolean };
  if (w.__devApiMockInstalled) return;
  w.__devApiMockInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);

    // Only intercept calls to our API base; everything else (Vite HMR, assets) passes through.
    if (!url.startsWith(API_BASE_URL)) return originalFetch(input, init);

    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const [path, queryString = ""] = url.slice(API_BASE_URL.length).split("?");
    const query = new URLSearchParams(queryString);

    let body: any;
    if (init?.body && typeof init.body === "string") {
      try {
        body = JSON.parse(init.body);
      } catch {
        body = undefined;
      }
    }

    const response = route(method, path, body, query);
    if (response) return response;
    return originalFetch(input, init);
  };
}
